import React from "react";
import { Switch } from "../../../../../ui/components/Switch";
import { FieldFootnote } from "./FieldFootnote";
import { FieldRowProps } from "./fieldTypes";

/**
 * ZOUR-19 — a yes/no answer rendered as a switch.
 *
 * The value stays a STRING ("1"/"0"), because that is what the column stores
 * and what every other reader of `company_gst_enabled` expects — the login
 * payload, the staged Excel uploads, the cockpit-core crons. Turning it into a
 * boolean here and back again at the edge would be two chances to get it wrong
 * for no gain.
 *
 * The current state is spelled out in words beside the switch. On a settings
 * page a lone switch leaves the reader deciding whether the highlighted
 * position means on or off, and this particular answer changes how every
 * document is pushed.
 */
export const ToggleFieldRow: React.FC<FieldRowProps> = ({
  config,
  value,
  locked,
  lockReason,
  errorMessage,
  options,
  onChange,
}) => {
  const checked = value === "1";
  // The server owns the vocabulary, so the label beside the switch is the
  // server's own wording for whichever value is selected.
  const currentLabel =
    options.find((option) => option.value === (checked ? "1" : "0"))?.label ??
    (checked ? "Yes" : "No");

  return (
    <div className="flex w-full flex-col gap-1">
      <span className="text-body-bold font-body-bold text-default-font">{config.label}</span>

      <div className="flex items-center gap-3 py-1">
        <Switch
          checked={checked}
          // Disabled AND guarded: `disabled` is what the user sees, the early
          // return is what holds if the control is driven some other way. The
          // server refuses the write regardless — locking is its decision, not
          // this component's.
          disabled={locked}
          onCheckedChange={(next: boolean) => {
            if (locked) {
              return;
            }

            onChange(next ? "1" : "0");
          }}
        />
        <span className="text-body font-body text-default-font">{currentLabel}</span>
      </div>

      <FieldFootnote
        hint={config.hint}
        example={config.example}
        lockReason={lockReason}
        errorMessage={errorMessage}
      />
    </div>
  );
};
